/**
 * Structured project-brief questionnaire — single source of truth for the
 * creation wizard's chip questions AND the contractor-facing spec sheet.
 *
 * Values live in projects.structured_brief (jsonb). Everything is optional:
 * the questionnaire nudges, it never blocks project creation.
 */

// Chip questions shown for FULL projects (multi-phase builds).
export const FULL_BRIEF_QUESTIONS = [
  {
    key: 'buildStage',
    label: 'Where is the build right now?',
    specLabel: 'Build stage',
    options: [
      { value: 'bare_ground', label: 'Starting from bare ground' },
      { value: 'foundation_done', label: 'Foundation already done' },
      { value: 'walls_up', label: 'Walls are up' },
      { value: 'roof_on', label: 'Roof is on — finishing left' },
    ],
  },
  {
    key: 'standStatus',
    label: 'The stand / plot',
    specLabel: 'Stand / plot',
    options: [
      { value: 'owned_title', label: 'I own it (title / cession)' },
      { value: 'family_land', label: 'Family or communal land' },
      { value: 'buying', label: 'Still buying it' },
    ],
  },
  {
    key: 'plansStatus',
    label: 'House plans',
    specLabel: 'House plans',
    options: [
      // specValueLabel: contractor-facing wording where the wizard label
      // contains in-wizard guidance that would read oddly on the spec sheet.
      { value: 'have_plans', label: 'I have plans (attach below)', specValueLabel: 'Owner has plans' },
      { value: 'plans_in_progress', label: 'Plans are being drawn' },
      { value: 'need_plans', label: 'I need help getting plans', specValueLabel: 'Owner needs help getting plans' },
    ],
  },
  {
    key: 'finishLevel',
    label: 'Finish level',
    specLabel: 'Finish level',
    options: [
      { value: 'basic', label: 'Basic' },
      { value: 'standard', label: 'Standard' },
      { value: 'premium', label: 'Premium' },
    ],
  },
  {
    key: 'materialsSupplier',
    label: 'Who buys materials?',
    specLabel: 'Materials',
    options: [
      { value: 'contractor_supplies', label: 'Contractor supplies materials' },
      { value: 'client_supplies', label: "I'll buy materials myself" },
      { value: 'discuss', label: "Let's discuss" },
    ],
  },
];

// Asked on every project (quick and full): how the client wants to pay.
// "stages" is what turns a scary total into a first step the client can
// fund — the answer travels to the contractor's quoting screen.
export const FUNDING_QUESTION = {
  key: 'fundingPreference',
  label: 'How would you like to pay for the work?',
  specLabel: 'Client wants to pay',
  options: [
    { value: 'stages', label: 'In stages, as each part is done', specValueLabel: 'In stages — quote it stage by stage' },
    { value: 'once', label: 'All at once', specValueLabel: 'All at once (staged quotation still welcome)' },
    { value: 'unsure', label: 'Not sure yet', specValueLabel: 'Not sure yet — a staged quotation helps them decide' },
  ],
};

export const FUNDING_LABELS = FUNDING_QUESTION.options.reduce((m, o) => ({ ...m, [o.value]: o.specValueLabel || o.label }), {});

// Free-text prompts (kept short deliberately — chips carry the structure).
export const FULL_BRIEF_TEXT_FIELDS = [
  {
    key: 'roomsSummary',
    label: 'Rooms & size',
    specLabel: 'Rooms & size',
    placeholder: 'e.g. 4 bedrooms, 2 bathrooms, open-plan kitchen — about 150 m²',
  },
];

export const QUICK_BRIEF_TEXT_FIELDS = [
  {
    key: 'measurements',
    label: 'Sizes & quantities',
    specLabel: 'Sizes & quantities',
    placeholder: 'e.g. 3 rooms about 4m × 4m each; 2 doors; roughly 60 m of wall',
  },
  {
    key: 'siteNotes',
    label: 'Site & access notes',
    specLabel: 'Site & access notes',
    placeholder: 'e.g. someone is home weekdays; water and power on site; narrow gate',
  },
];

export const TIMELINE_LABELS = {
  asap: 'ASAP',
  '1month': 'Within 1 month',
  '3months': 'Within 3 months',
  flexible: 'Flexible',
  specific: 'Specific date',
};

const OPTION_LABELS = FULL_BRIEF_QUESTIONS.reduce((acc, q) => {
  acc[q.key] = q.options.reduce((m, o) => ({ ...m, [o.value]: o.specValueLabel || o.label }), {});
  return acc;
}, {});

const TEXT_SPEC_LABELS = [...FULL_BRIEF_TEXT_FIELDS, ...QUICK_BRIEF_TEXT_FIELDS]
  .reduce((acc, f) => ({ ...acc, [f.key]: f.specLabel }), {});

export const parseStructuredBrief = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

// How many questionnaire answers the client actually gave (used for the
// client-facing readiness nudge — timeline/priorities have defaults, so they
// don't count).
export const answeredBriefCount = (brief) => {
  if (!brief) return 0;
  const answerKeys = [
    ...FULL_BRIEF_QUESTIONS.map((q) => q.key),
    ...Object.keys(TEXT_SPEC_LABELS),
  ];
  return answerKeys.filter((k) => String(brief[k] || '').trim()).length;
};

/**
 * Flatten a structured brief into ordered { label, value } display rows for
 * the contractor spec sheet. Internal fields (version, ownerNote) are skipped.
 */
export const structuredBriefRows = (raw) => {
  const brief = parseStructuredBrief(raw);
  if (!brief) return [];
  const rows = [];

  FULL_BRIEF_QUESTIONS.forEach((q) => {
    const v = brief[q.key];
    if (v) rows.push({ label: q.specLabel, value: OPTION_LABELS[q.key]?.[v] || v });
  });

  Object.entries(TEXT_SPEC_LABELS).forEach(([key, specLabel]) => {
    const v = String(brief[key] || '').trim();
    if (v) rows.push({ label: specLabel, value: v });
  });

  if (brief.fundingPreference && FUNDING_LABELS[brief.fundingPreference]) {
    rows.push({ label: FUNDING_QUESTION.specLabel, value: FUNDING_LABELS[brief.fundingPreference] });
  }

  if (brief.timelineType) {
    const label = TIMELINE_LABELS[brief.timelineType] || brief.timelineType;
    rows.push({
      label: 'Preferred start',
      value: brief.timelineType === 'specific' && brief.startDate ? `${brief.startDate}` : label,
    });
  }

  if (Array.isArray(brief.priorities) && brief.priorities.length > 0) {
    rows.push({ label: 'What matters most', value: brief.priorities.join(', ') });
  }

  return rows;
};
